import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { AgentMark } from '../../ui/AgentMark';
import { ProcessingTag } from '../../ui/ProcessingTag';
import { useDb } from '../../../hooks/useDb';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import { getAgent } from '../../../utils/agents';
import type { AgentId } from '../../../types/anna';
import type { WallGeometry } from '../../../utils/orbitLayout';
interface SatelliteThreadProps {
  agent: AgentId;
  /** Satellite position when tapped */
  x: number;
  y: number;
  g: WallGeometry;
  onClose: () => void;
}
const W = 440;
const EASE = [0.23, 1, 0.32, 1] as const;

/** A satellite's live thread, unfolded next to it: task, step, model, and the latest events streaming in. */
export function SatelliteThread({
  agent,
  x,
  y,
  g,
  onClose
}: SatelliteThreadProps) {
  const task = useDb(t => t.agentTasks.find(a => a.agent === agent), [agent]);
  const reduce = useMotionPreference();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (ref.current?.contains(target) || target?.closest?.('[data-satellite]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [onClose]);
  if (!task) return null;
  const a = getAgent(agent);
  const leftSide = x < g.cx;
  const left = leftSide ? Math.max(24, x - W - 32) : Math.min(g.w - W - 24, x + 32);
  const top = Math.max(150, Math.min(g.h - 480, y - 90));
  const recent = task.events.slice(-3);
  const offset = task.events.length - recent.length;
  return <motion.div ref={ref} role="dialog" aria-label={`${a.name} aan het werk`} className="glass absolute z-40 rounded-[28px] p-6" style={{
    left,
    top,
    width: W,
    transformOrigin: leftSide ? '100% 30%' : '0% 30%'
  }} initial={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    scale: 0.96
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    scale: 0.96
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} data-id="element-1683" data-mp="1y6ugrmkdq16d">
      <div className="flex items-center gap-3" data-id="element-1684" data-mp="1jgvizkwqkrrf">
        <AgentMark agentId={agent} data-id="element-1685" data-mp="15gcdakfqtxsf" />
        <p className="flex-1 text-[22px] font-medium" style={{
        color: a.color
      }} data-id="element-1686" data-mp="15hdpwtceqsc9">
          {a.name}
        </p>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Sluiten" className="grid h-12 w-12 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/10 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-1687" data-mp="1af5rkf2utstn">
          <XIcon className="h-6 w-6" data-id="element-1688" data-mp="39yk0u7rzrop1" />
        </button>
      </div>
      <h3 className="mt-2 text-[26px] font-semibold leading-tight text-ink" data-id="element-1689" data-mp="3np5ijt5plslc">{task.title}</h3>
      <p className={`mt-2 text-[22px] ${task.status === 'wacht' ? 'text-amber' : 'text-ink-soft'}`} data-id="element-1690" data-mp="3nr08xmzr2cwt">
        stap {task.step}/{task.totalSteps} · {task.stepLabel}
      </p>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full" style={{
      backgroundColor: `${a.color}26`
    }} data-id="element-1691" data-mp="1g4b7ov4fpnn">
        <motion.div className="h-full rounded-full" style={{
        backgroundColor: a.color
      }} initial={false} animate={{
        width: `${task.step / task.totalSteps * 100}%`
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-1692" data-mp="3sqcs9xrex1un" />
      </div>
      <ul className="mt-4 space-y-1 border-l border-white/10 pl-4 font-mono text-[22px] leading-snug" aria-live="polite" data-id="element-1693" data-mp="2caij45qnr8j5">
        <AnimatePresence initial={false} data-id="element-1694" data-mp="28ahrtkign2uo">
          {recent.map((e, i) => <motion.li key={`${offset + i}-${e}`} layout={!reduce} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.12
          }
        }} transition={{
          duration: 0.2,
          ease: EASE
        }} className={i === recent.length - 1 ? 'text-ink' : 'text-ink-muted'} data-id="element-1695" data-mp="29p2indauahy2" data-mpi={i}>
              {e}
            </motion.li>)}
        </AnimatePresence>
      </ul>
      <div className="mt-4 flex items-center gap-3 border-t border-white/[0.07] pt-4 text-[22px] text-ink-muted" data-id="element-1696" data-mp="1uybnpwdyb2qq">
        <span className="min-w-0 flex-1 truncate" data-id="element-1697" data-mp="24qhex3wdpg4v">{task.model}</span>
        <ProcessingTag where={task.where} size="wall" data-id="element-1698" data-mp="24rsib2lwwlvg" />
      </div>
    </motion.div>;
}